import * as React from 'react'
import { connect } from 'react-redux'
import { RootState } from 'pc/reducer'
import { KeyCode } from 'common/constants/keyCode'
import { styling, debounce } from 'common/utils'
import { DynamicCx } from 'common/types'
import {
  AutoCompleteState,
  toggleAutoComplete,
  activateAutoComplete,
  openAutoComplete,
  closeAutoComplete,
  fetchAutoComplete,
} from 'common/ducks/gnb/autoComplete'
import * as s from './searchInput.scss'

interface StateProps {
  autoComplete: AutoCompleteState
}

interface DispatchProps {
  toggleAutoComplete: typeof toggleAutoComplete
  activateAutoComplete: typeof activateAutoComplete
  openAutoComplete: typeof openAutoComplete
  closeAutoComplete: typeof closeAutoComplete
  fetchAutoComplete: typeof fetchAutoComplete
}

interface OwnProps {
  cx?: DynamicCx
  className?: string
  title?: string
  placeHolder?: string
  onSearch: Function
  onResetSelectedKeyword: Function
  selectedKeyword: string
}

interface OwnState {
  selectedKeyword: string
  currentKeyword: string
}

type Props = StateProps & DispatchProps & OwnProps

@styling(s)
class SearchInput extends React.Component<Props, OwnState> {
  handleFetchAutocompleteDebounced: Function
  inputRef: React.RefObject<HTMLInputElement>

  constructor(props) {
    super(props)

    // state 저장되기 전에 검색시에 간헐적으로 이전 키워드로 검색되므로 input에 있는 값으로 검색하기 위함
    this.inputRef = React.createRef()
    this.state = { selectedKeyword: '', currentKeyword: '' }
    this.handleFetchAutocompleteDebounced = debounce(this.handleFetchAutocomplete, 50)
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    return {
      selectedKeyword: nextProps.selectedKeyword,
    }
  }

  // input의 특정 키의 keydown 이벤트는 무시한다 (shift + tab, tab, down, up key)
  // - enter키 누를시에 검색 실행
  // - 자동완성 리스트에서 선택된 값이 input에 임시로 반영되고, keydown클릭시에 다른 검색어를 입력할 수 있는 상태로 전환해주기
  handleKeyDownInput = async e => {
    const { selectedKeyword } = this.state

    if (e.shiftKey || e.keyCode === KeyCode.TAB || e.keyCode === KeyCode.DOWN_ARROW || e.keyCode === KeyCode.UP_ARROW) {
      return
    }

    if (e.keyCode === KeyCode.ENTER) {
      e.preventDefault()
      this.props.onSearch({ keyword: this.inputRef.current.value, category: '' })
      return
    }

    const currentInputValue = e.currentTarget.value
    if (currentInputValue === selectedKeyword) {
      this.setState({ currentKeyword: currentInputValue }, () => this.props.onResetSelectedKeyword())
    }
  }

  handleChangeInput = async e => {
    const currentKeyword = e.currentTarget.value
    this.setState({ currentKeyword }, () => {
      if (!currentKeyword) this.props.closeAutoComplete()
    })
  }

  handleKeyUpInput = async e => {
    const { autoComplete } = this.props
    const { selectedKeyword } = this.state

    if (!autoComplete.isActive) return

    // 자동완성에서 선택된 키워드가 있는 경우 데이터 fetch를 하지 않음
    if (selectedKeyword) return
    this.handleFetchAutocompleteDebounced()
  }

  handleFetchAutocomplete = async () => {
    const { autoComplete, fetchAutoComplete, openAutoComplete, closeAutoComplete } = this.props
    const { isOpen, keyword } = autoComplete

    const fetchKeyword = this.state.currentKeyword

    // input클릭시에 자동완성 레이어 toggle. 그때마다 호출되는 것을 막기 위함
    if (this.state.currentKeyword !== keyword) {
      await Promise.all([fetchAutoComplete(fetchKeyword)])

      if (this.isNotExistResult()) {
        closeAutoComplete()
        return
      }

      if (!isOpen) openAutoComplete()
    }
  }

  handleClickInput = e => {
    const { autoComplete, toggleAutoComplete } = this.props
    const { keyword } = autoComplete
    const currentKeyword = e.currentTarget.value

    // 키워드가 없는 경우에는 안내 레이어 노출. (데이터 없는 경우로 check)
    // 키워드가 있지만, 자동완성 데이터가 없는 경우에는 자동완성 레이어 안보여져야 한다.
    if (keyword && this.isNotExistResult()) return
    if (!autoComplete.isActive) return

    this.setState({ currentKeyword }, () => {
      this.handleFetchAutocompleteDebounced()
      toggleAutoComplete()
    })
  }

  handleClickAutoComplete = () => {
    const { autoComplete, openAutoComplete, activateAutoComplete, toggleAutoComplete } = this.props
    const { keyword } = autoComplete

    if (keyword && this.isNotExistResult()) return

    if (autoComplete.isActive) {
      toggleAutoComplete()
      return
    }

    this.handleFetchAutocompleteDebounced()
    activateAutoComplete()
    openAutoComplete()
  }

  // 자동완성 데이터가 존재하는지 검사
  isNotExistResult = () => {
    const { autoComplete } = this.props
    return autoComplete.totalCount < 1
  }

  goSearch = () => {
    const target = this.inputRef.current
    this.props.onSearch({ keyword: target.value, category: '' })
  }

  render() {
    const { className, title, placeHolder, autoComplete, cx } = this.props
    const { selectedKeyword, currentKeyword } = this.state

    return (
      <>
        <input
          type="text"
          className={cx(className)}
          title={title}
          placeholder={placeHolder}
          value={selectedKeyword || currentKeyword}
          onKeyDown={this.handleKeyDownInput}
          onChange={this.handleChangeInput}
          onKeyUp={this.handleKeyUpInput}
          onClick={this.handleClickInput}
          ref={this.inputRef}
        />
        <button type="button" className={cx('btn_search_auto')} onClick={this.handleClickAutoComplete}>
          {!autoComplete.isActive ? (
            <span className={cx('more', 'none')}>자동완성 기능끄기</span>
          ) : autoComplete.isOpen ? (
            <span className={cx('more', 'on')}>자동완성 접기</span>
          ) : (
            <span className={cx('more')}>자동완성 펼치기</span>
          )}
        </button>
        <div className={cx('layer_tooltip')}>
          <span className={cx('inner')}>
            {!autoComplete.isActive ? '자동완성 켜기' : autoComplete.isOpen ? '자동완성 접기' : '자동완성 펼치기'}
          </span>
        </div>
        <button className={cx('btn_search')} onClick={this.goSearch}>
          네이버쇼핑 검색
        </button>
      </>
    )
  }
}

export default connect<StateProps, DispatchProps, OwnProps>(
  (state: RootState) => ({
    autoComplete: state.autoComplete,
  }),
  {
    toggleAutoComplete,
    activateAutoComplete,
    openAutoComplete,
    closeAutoComplete,
    fetchAutoComplete,
  },
)(SearchInput)
